/**
 * Step4ReviewSend Component
 *
 * Summary of email configuration and send progress.
 * Shows:
 * - Selected events by category
 * - Total recipients
 * - Send mode
 * - Progress bar during sending
 * - Success/error states
 * - Manual "Mark as Completed" button for EPG tracking
 *
 * Updated: 2026-01-13 - Added EPG event completion marking after successful send
 * Updated: 2026-01-13 - Added compact mobile UI with single-line elements
 * Updated: 2026-01-13 - Added manual "Mark as Completed" button for manual sends
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  ChevronLeft,
  Send,
  Loader2,
  CheckCircle,
  AlertCircle,
  Trophy,
  Music,
  Calendar,
  User,
  Users,
  Mail,
  Clock,
  FlaskConical,
  CheckSquare,
} from 'lucide-react';
import { useIsMobile } from '@shared/hooks/useIsMobile';
import { epgApi } from '@shared/services/epgApi';
import { logger } from '@shared/utils/logger';
import type { HMCEvent } from '../../hmcEventsStore';
import type { EPGContactWithOrg } from '../../hooks/useEpgContactFilter';

export interface SendProgress {
  status: 'idle' | 'sending' | 'complete' | 'error';
  sent: number;
  total: number;
  errors: Array<{ email: string; error: string }>;
}

interface Step4ReviewSendProps {
  /** Selected events for the email */
  selectedEvents: HMCEvent[];
  /** Selected recipients */
  selectedContacts: EPGContactWithOrg[];
  /** Send mode */
  sendMode: 'individual' | 'bulk-bcc';
  /** BCC To email (for bulk mode) */
  bccToEmail: string;
  /** Email subject */
  emailSubject: string;
  /** Send progress state */
  sendProgress: SendProgress;
  /** Test mode enabled */
  testModeEnabled?: boolean;
  /** Test email addresses */
  testEmails?: string[];
  /** Callback to start sending */
  onSend: () => void;
  /** Navigation */
  onBack: () => void;
  /** Close modal when done */
  onDone: () => void;
}

// Clean event name for display
function getCleanEventName(opponent: string | null): string {
  if (!opponent) return 'Event';
  const cleaned = opponent
    .replace(/\s*[-–]\s*\d+\s*(tickets?|tix|seats?).*$/i, '')
    .replace(/\s*\(\d+\s*(tickets?|tix|seats?)\).*$/i, '')
    .trim();
  return cleaned || 'Event';
}

// Categorize events for summary
function categorizeEvents(events: HMCEvent[]): {
  sporting: HMCEvent[];
  concerts: HMCEvent[];
  other: HMCEvent[];
} {
  const sporting: HMCEvent[] = [];
  const concerts: HMCEvent[] = [];
  const other: HMCEvent[] = [];

  for (const event of events) {
    if (
      event.event_type === 'dynamo' ||
      event.event_type === 'dash' ||
      event.opponent?.toLowerCase().includes('rockets') ||
      event.opponent?.toLowerCase().includes('astros') ||
      event.opponent?.toLowerCase().includes('texans')
    ) {
      sporting.push(event);
    } else if (event.music_genre && event.music_genre !== 'other') {
      concerts.push(event);
    } else {
      other.push(event);
    }
  }

  return { sporting, concerts, other };
}

export const Step4ReviewSend: React.FC<Step4ReviewSendProps> = ({
  selectedEvents,
  selectedContacts,
  sendMode,
  bccToEmail,
  emailSubject,
  sendProgress,
  testModeEnabled = false,
  testEmails = [],
  onSend,
  onBack,
  onDone,
}) => {
  // Ref to prevent double-marking completions
  const hasMarkedComplete = useRef(false);
  const isMobile = useIsMobile();

  // State for manual completion marking
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const [markCompleteStatus, setMarkCompleteStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Get valid test emails
  const validTestEmails = testEmails.filter((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()));
  const categories = categorizeEvents(selectedEvents);
  const progressPercent =
    sendProgress.total > 0 ? Math.round((sendProgress.sent / sendProgress.total) * 100) : 0;

  // Estimate time for individual mode
  const estimatedMinutes = Math.ceil((selectedContacts.length * 0.5) / 60);
  const estimatedTimeText =
    sendMode === 'individual'
      ? estimatedMinutes < 1
        ? 'less than a minute'
        : estimatedMinutes === 1
        ? '~1 minute'
        : `~${estimatedMinutes} minutes`
      : 'Instant';

  /**
   * Mark events as completed on EPG
   * Used both automatically after successful send and manually via button
   * @param isManual - If true, this is a manual marking (shows UI feedback)
   */
  const markEventsAsCompleted = async (isManual = false) => {
    // For automatic marking: Don't mark completions if in test mode
    if (!isManual && testModeEnabled && validTestEmails.length > 0) {
      logger.log('[Step4ReviewSend] Skipping completion marking - test mode enabled');
      return;
    }

    // Require at least one event and one contact
    if (selectedEvents.length === 0 || selectedContacts.length === 0) {
      logger.warn('[Step4ReviewSend] No events or contacts to mark as completed');
      if (isManual) {
        setMarkCompleteStatus('error');
      }
      return;
    }

    if (isManual) {
      setIsMarkingComplete(true);
      setMarkCompleteStatus('idle');
    }

    try {
      // Get current month in YYYY-MM format
      const currentMonth = new Date().toISOString().slice(0, 7);

      // Group contacts by organization and create completion records
      // Each unique org-event combination gets marked as completed
      const orgEventPairs = new Map<string, { orgId: number; eventId: number }>();

      for (const contact of selectedContacts) {
        for (const event of selectedEvents) {
          const key = `${contact.organization_id}-${event.id}`;
          if (!orgEventPairs.has(key)) {
            orgEventPairs.set(key, {
              orgId: contact.organization_id,
              eventId: event.id,
            });
          }
        }
      }

      const completions = Array.from(orgEventPairs.values()).map((pair) => ({
        orgId: pair.orgId,
        eventId: pair.eventId,
        fieldKey: 'email_invite_sent',
        month: currentMonth,
        campaignType: isManual ? 'manual_completion' : 'email_invite',
        metadata: {
          contactsCount: selectedContacts.filter((c) => c.organization_id === pair.orgId).length,
          sendMode: isManual ? 'manual' : sendMode,
          sentAt: new Date().toISOString(),
          emailSubject,
          markedManually: isManual,
        },
      }));

      if (completions.length > 0) {
        await epgApi.bulkMarkCompleted(completions);
        logger.log(`[Step4ReviewSend] Marked ${completions.length} events as completed on EPG (manual: ${isManual})`);

        if (isManual) {
          setMarkCompleteStatus('success');
          // Auto-clear success after 3 seconds
          setTimeout(() => setMarkCompleteStatus('idle'), 3000);
        }
      }
    } catch (error) {
      logger.error('[Step4ReviewSend] Failed to mark events as completed:', error);
      if (isManual) {
        setMarkCompleteStatus('error');
      }
    } finally {
      if (isManual) {
        setIsMarkingComplete(false);
      }
    }
  };

  /**
   * Handle manual "Mark as Completed" button click
   * Allows users to mark completions without sending emails (e.g., sent manually via Outlook)
   */
  const handleManualMarkComplete = () => {
    markEventsAsCompleted(true);
  };

  // Mark events as completed when sending completes successfully
  useEffect(() => {
    if (sendProgress.status === 'complete' && !hasMarkedComplete.current) {
      hasMarkedComplete.current = true;
      markEventsAsCompleted();
    }
  }, [sendProgress.status]);

  // Reset the completion marker when the component remounts or status changes to idle
  useEffect(() => {
    if (sendProgress.status === 'idle') {
      hasMarkedComplete.current = false;
    }
  }, [sendProgress.status]);

  // Helper to get truncated event names
  const getEventNamesList = (events: HMCEvent[], maxChars = 50) => {
    const names = events.map((e) => getCleanEventName(e.opponent));
    const joined = names.join(', ');
    if (joined.length <= maxChars) return joined;
    return joined.substring(0, maxChars) + '...';
  };

  // Mobile compact UI
  if (isMobile) {
    return (
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="px-3 py-2 border-b border-dark-border/30">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-white font-medium">Review & Send</span>
          </div>
        </div>

        {/* Compact content */}
        <div className="flex-1 px-3 py-2 overflow-y-auto space-y-2">
          {/* Idle state - summary info */}
          {sendProgress.status === 'idle' && (
            <>
              {/* Test Mode Warning - Compact */}
              {testModeEnabled && validTestEmails.length > 0 && (
                <div className="flex items-center gap-2 p-2 bg-amber-500/10 border border-amber-500/50 rounded-lg">
                  <FlaskConical className="w-4 h-4 text-amber-400 shrink-0" />
                  <span className="text-xs text-amber-300">
                    TEST: {validTestEmails.join(', ')}
                  </span>
                </div>
              )}

              {/* Events - Single Line */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-dark-muted whitespace-nowrap w-20">Events:</span>
                <span className="text-xs text-white truncate flex-1">
                  {selectedEvents.length} selected
                </span>
              </div>

              {/* Event breakdown by category - compact inline */}
              {categories.sporting.length > 0 && (
                <div className="flex items-center gap-2 pl-[88px]">
                  <Trophy className="w-3 h-3 text-orange-400 shrink-0" />
                  <span className="text-xs text-dark-text truncate">
                    {getEventNamesList(categories.sporting, 40)}
                  </span>
                </div>
              )}
              {categories.concerts.length > 0 && (
                <div className="flex items-center gap-2 pl-[88px]">
                  <Music className="w-3 h-3 text-purple-400 shrink-0" />
                  <span className="text-xs text-dark-text truncate">
                    {getEventNamesList(categories.concerts, 40)}
                  </span>
                </div>
              )}
              {categories.other.length > 0 && (
                <div className="flex items-center gap-2 pl-[88px]">
                  <Calendar className="w-3 h-3 text-cyan-400 shrink-0" />
                  <span className="text-xs text-dark-text truncate">
                    {getEventNamesList(categories.other, 40)}
                  </span>
                </div>
              )}

              {/* Recipients - Single Line */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-dark-muted whitespace-nowrap w-20">Recipients:</span>
                <span className="text-xs text-white">
                  {selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Mode - Single Line */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-dark-muted whitespace-nowrap w-20">Mode:</span>
                <span
                  className={`px-2 py-0.5 rounded text-xs ${
                    sendMode === 'individual'
                      ? 'bg-purple-500/20 text-purple-400'
                      : 'bg-blue-500/20 text-blue-400'
                  }`}
                >
                  {sendMode === 'individual' ? 'Individual' : 'Bulk BCC'}
                </span>
                {sendMode === 'bulk-bcc' && (
                  <span className="text-xs text-dark-muted truncate">
                    → {bccToEmail}
                  </span>
                )}
              </div>

              {/* Time - Single Line */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-dark-muted whitespace-nowrap w-20">Est. time:</span>
                <span className="text-xs text-dark-text">{estimatedTimeText}</span>
              </div>

              {/* Subject - Single Line */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-dark-muted whitespace-nowrap w-20">Subject:</span>
                <span className="text-xs text-dark-text truncate flex-1">
                  {emailSubject || '(No subject)'}
                </span>
              </div>
            </>
          )}

          {/* Sending state - Compact */}
          {sendProgress.status === 'sending' && (
            <div className="p-4 bg-dark-bg border border-dark-border/30 rounded-xl text-center">
              <Loader2 className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-2" />
              <p className="text-sm text-white mb-2">
                {sendMode === 'individual'
                  ? `${sendProgress.sent}/${sendProgress.total}`
                  : 'Sending...'}
              </p>
              <div className="w-full bg-dark-border rounded-full h-1.5 mb-1">
                <div
                  className="bg-purple-500 h-1.5 rounded-full transition-all duration-200"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className="text-xs text-dark-muted">{progressPercent}%</span>
            </div>
          )}

          {/* Success state - Compact */}
          {sendProgress.status === 'complete' && (
            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
              <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="text-sm text-white mb-1">Sent!</p>
              <p className="text-xs text-dark-muted">
                {sendMode === 'individual'
                  ? `${sendProgress.sent} emails sent`
                  : `Sent to ${selectedContacts.length} recipients`}
              </p>
              {sendProgress.errors.length > 0 && (
                <p className="text-xs text-red-400 mt-2">
                  {sendProgress.errors.length} failed
                </p>
              )}
            </div>
          )}

          {/* Error state - Compact */}
          {sendProgress.status === 'error' && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-center">
              <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-sm text-white mb-1">Failed</p>
              <p className="text-xs text-dark-muted">
                {sendProgress.errors[0]?.error || 'Unknown error'}
              </p>
            </div>
          )}
        </div>

        {/* Footer navigation - Compact */}
        <div className="px-3 py-2 border-t border-dark-border/30">
          {sendProgress.status === 'idle' && (
            <div className="flex flex-col gap-2">
              {/* Manual completion status message */}
              {markCompleteStatus === 'success' && (
                <div className="flex items-center gap-2 px-2 py-1.5 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                  <span className="text-xs text-green-400">
                    Marked as completed on EPG calendar
                  </span>
                </div>
              )}
              {markCompleteStatus === 'error' && (
                <div className="flex items-center gap-2 px-2 py-1.5 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                  <span className="text-xs text-red-400">
                    Failed to mark as completed
                  </span>
                </div>
              )}

              {/* Button row */}
              <div className="flex items-center justify-between">
                <button
                  onClick={onBack}
                  className="flex items-center gap-1 px-3 py-2 text-dark-muted hover:text-white text-sm"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>

                <div className="flex items-center gap-2">
                  {/* Mark as Completed button */}
                  <button
                    onClick={handleManualMarkComplete}
                    disabled={isMarkingComplete || markCompleteStatus === 'success'}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                      markCompleteStatus === 'success'
                        ? 'bg-green-500/20 text-green-400 cursor-default'
                        : 'bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 border border-cyan-500/30'
                    }`}
                    title="Mark as completed on EPG (for emails sent manually)"
                  >
                    {isMarkingComplete ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : markCompleteStatus === 'success' ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <CheckSquare className="w-4 h-4" />
                    )}
                    {markCompleteStatus === 'success' ? 'Done' : 'Mark Complete'}
                  </button>

                  {/* Send button */}
                  <button
                    onClick={onSend}
                    className={`flex items-center gap-1.5 px-4 py-2 text-white rounded-lg text-sm font-medium ${
                      testModeEnabled && validTestEmails.length > 0
                        ? 'bg-amber-500 hover:bg-amber-600'
                        : 'bg-green-500 hover:bg-green-600'
                    }`}
                  >
                    {testModeEnabled && validTestEmails.length > 0 ? (
                      <>
                        <FlaskConical className="w-4 h-4" />
                        Test
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send ({selectedContacts.length})
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {sendProgress.status === 'sending' && (
            <div className="w-full text-center text-xs text-dark-muted">
              Sending...
            </div>
          )}

          {sendProgress.status === 'complete' && (
            <div className="w-full flex justify-center">
              <button
                onClick={onDone}
                className="px-6 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600"
              >
                Done
              </button>
            </div>
          )}

          {sendProgress.status === 'error' && (
            <div className="w-full flex items-center justify-between">
              <button
                onClick={onBack}
                className="flex items-center gap-1 px-3 py-2 text-dark-muted hover:text-white text-sm"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
              <button
                onClick={onSend}
                className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600"
              >
                Retry
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop UI (original)
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-3 sm:p-4 border-b border-dark-border/30">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-purple-400" />
          <span className="text-sm text-white font-medium">Review & Send</span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-4 sm:p-6 overflow-y-auto">
        <div className="max-w-2xl mx-auto space-y-3 sm:space-y-6">
          {/* Summary Cards */}
          {sendProgress.status === 'idle' && (
            <>
              {/* Test Mode Warning Banner */}
              {testModeEnabled && validTestEmails.length > 0 && (
                <div className="p-3 sm:p-4 bg-amber-500/10 border-2 border-amber-500/50 rounded-xl">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <FlaskConical className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="text-xs sm:text-sm font-semibold text-amber-400 mb-1">
                        TEST MODE ACTIVE
                      </h3>
                      <p className="text-xs text-amber-300/80 mb-2">
                        Emails will be sent ONLY to the test addresses below, NOT to the {selectedContacts.length} actual recipient{selectedContacts.length !== 1 ? 's' : ''}.
                      </p>
                      <div className="flex flex-wrap gap-1.5 sm:gap-2">
                        {validTestEmails.map((email, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-amber-500/20 text-amber-300 rounded text-xs font-medium"
                          >
                            {email}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Events Summary */}
              <div className="p-3 sm:p-4 bg-dark-bg border border-dark-border/30 rounded-xl">
                <h3 className="text-sm font-medium text-white mb-2 sm:mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-purple-400" />
                  Events ({selectedEvents.length})
                </h3>
                <div className="space-y-1.5 sm:space-y-2">
                  {categories.sporting.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Trophy className="w-3 h-3 text-orange-400" />
                      <span className="text-xs text-dark-muted">Sporting:</span>
                      <span className="text-xs text-white">
                        {categories.sporting.map((e) => getCleanEventName(e.opponent)).join(', ')}
                      </span>
                    </div>
                  )}
                  {categories.concerts.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Music className="w-3 h-3 text-purple-400" />
                      <span className="text-xs text-dark-muted">Concerts:</span>
                      <span className="text-xs text-white">
                        {categories.concerts.map((e) => getCleanEventName(e.opponent)).join(', ')}
                      </span>
                    </div>
                  )}
                  {categories.other.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3 h-3 text-cyan-400" />
                      <span className="text-xs text-dark-muted">Other:</span>
                      <span className="text-xs text-white">
                        {categories.other.map((e) => getCleanEventName(e.opponent)).join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Recipients Summary */}
              <div className="p-3 sm:p-4 bg-dark-bg border border-dark-border/30 rounded-xl">
                <h3 className="text-sm font-medium text-white mb-2 sm:mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-400" />
                  Recipients ({selectedContacts.length})
                </h3>
                <div className="max-h-24 sm:max-h-32 overflow-y-auto space-y-1">
                  {selectedContacts.slice(0, 10).map((contact) => (
                    <div key={contact.person_id} className="text-xs text-dark-muted">
                      {contact.first_name} {contact.last_name} ({contact.organization_name})
                    </div>
                  ))}
                  {selectedContacts.length > 10 && (
                    <div className="text-xs text-dark-muted">
                      ...and {selectedContacts.length - 10} more
                    </div>
                  )}
                </div>
              </div>

              {/* Send Mode Summary */}
              <div className="p-3 sm:p-4 bg-dark-bg border border-dark-border/30 rounded-xl">
                <h3 className="text-sm font-medium text-white mb-2 sm:mb-3 flex items-center gap-2">
                  {sendMode === 'individual' ? (
                    <User className="w-4 h-4 text-purple-400" />
                  ) : (
                    <Users className="w-4 h-4 text-blue-400" />
                  )}
                  Send Mode
                </h3>
                <div className="space-y-1.5 sm:space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-dark-muted">Mode:</span>
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        sendMode === 'individual'
                          ? 'bg-purple-500/20 text-purple-400'
                          : 'bg-blue-500/20 text-blue-400'
                      }`}
                    >
                      {sendMode === 'individual' ? 'Individual Emails' : 'Bulk BCC'}
                    </span>
                  </div>
                  {sendMode === 'individual' ? (
                    <div className="text-xs text-dark-muted">
                      Each recipient gets a personalized email with their first name.
                    </div>
                  ) : (
                    <div className="text-xs text-dark-muted">
                      Single email to <span className="text-blue-400">{bccToEmail}</span> with all
                      recipients in BCC.
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-xs text-dark-muted">
                    <Clock className="w-3 h-3" />
                    <span>Estimated time: {estimatedTimeText}</span>
                  </div>
                </div>
              </div>

              {/* Subject Summary */}
              <div className="p-3 sm:p-4 bg-dark-bg border border-dark-border/30 rounded-xl">
                <h3 className="text-sm font-medium text-white mb-1.5 sm:mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-dark-muted" />
                  Subject Line
                </h3>
                <p className="text-xs sm:text-sm text-dark-text">{emailSubject || '(No subject)'}</p>
              </div>
            </>
          )}

          {/* Progress UI */}
          {sendProgress.status === 'sending' && (
            <div className="p-4 sm:p-6 bg-dark-bg border border-dark-border/30 rounded-xl text-center">
              <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 text-purple-400 animate-spin mx-auto mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-semibold text-white mb-2">
                Sending Emails...
              </h3>
              <p className="text-xs sm:text-sm text-dark-muted mb-3 sm:mb-4">
                {sendMode === 'individual'
                  ? `Sending ${sendProgress.sent} of ${sendProgress.total}...`
                  : 'Sending bulk email...'}
              </p>
              {/* Progress bar */}
              <div className="w-full bg-dark-border rounded-full h-2 mb-2">
                <div
                  className="bg-purple-500 h-2 rounded-full transition-all duration-200"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className="text-xs text-dark-muted">{progressPercent}% complete</span>
            </div>
          )}

          {/* Success UI */}
          {sendProgress.status === 'complete' && (
            <div className="p-4 sm:p-6 bg-green-500/10 border border-green-500/30 rounded-xl text-center">
              <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-green-400 mx-auto mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-semibold text-white mb-2">
                Emails Sent Successfully!
              </h3>
              <p className="text-xs sm:text-sm text-dark-muted">
                {sendMode === 'individual'
                  ? `${sendProgress.sent} personalized emails were sent.`
                  : `Bulk email sent to ${selectedContacts.length} recipients.`}
              </p>
              {sendProgress.errors.length > 0 && (
                <div className="mt-3 sm:mt-4 text-left p-2.5 sm:p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-xs text-red-400 font-medium mb-2">
                    {sendProgress.errors.length} email(s) failed:
                  </p>
                  <ul className="text-xs text-dark-muted space-y-1">
                    {sendProgress.errors.slice(0, 5).map((err, i) => (
                      <li key={i}>
                        {err.email}: {err.error}
                      </li>
                    ))}
                    {sendProgress.errors.length > 5 && (
                      <li>...and {sendProgress.errors.length - 5} more</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Error UI */}
          {sendProgress.status === 'error' && (
            <div className="p-4 sm:p-6 bg-red-500/10 border border-red-500/30 rounded-xl text-center">
              <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 text-red-400 mx-auto mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-semibold text-white mb-2">
                Failed to Send Emails
              </h3>
              <p className="text-xs sm:text-sm text-dark-muted">
                {sendProgress.errors[0]?.error || 'An unknown error occurred.'}
              </p>
              <button
                onClick={onSend}
                className="mt-3 sm:mt-4 px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-all duration-150"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Footer with navigation */}
      <div className="p-3 sm:p-4 border-t border-dark-border/30">
        {sendProgress.status === 'idle' && (
          <div className="flex flex-col gap-2">
            {/* Manual completion status message */}
            {markCompleteStatus === 'success' && (
              <div className="flex items-center justify-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                <span className="text-sm text-green-400">
                  Events marked as completed on EPG calendar
                </span>
              </div>
            )}
            {markCompleteStatus === 'error' && (
              <div className="flex items-center justify-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                <span className="text-sm text-red-400">
                  Failed to mark events as completed
                </span>
              </div>
            )}

            {/* Button row */}
            <div className="flex items-center justify-between">
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 text-dark-muted hover:text-white transition-all duration-150 text-sm"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back</span>
              </button>

              <div className="flex items-center gap-3">
                {/* Mark as Completed button */}
                <button
                  onClick={handleManualMarkComplete}
                  disabled={isMarkingComplete || markCompleteStatus === 'success'}
                  className={`flex items-center gap-2 px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-150 ${
                    markCompleteStatus === 'success'
                      ? 'bg-green-500/20 text-green-400 cursor-default'
                      : 'bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 border border-cyan-500/30'
                  }`}
                  title="Mark events as completed on EPG calendar (for emails sent manually via Outlook, etc.)"
                >
                  {isMarkingComplete ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : markCompleteStatus === 'success' ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <CheckSquare className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">
                    {markCompleteStatus === 'success' ? 'Marked Complete' : 'Mark as Completed'}
                  </span>
                  <span className="sm:hidden">
                    {markCompleteStatus === 'success' ? 'Done' : 'Complete'}
                  </span>
                </button>

                {/* Send button */}
                <button
                  onClick={onSend}
                  className={`flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-2.5 text-white rounded-lg text-xs sm:text-sm font-semibold transition-all duration-150 ${
                    testModeEnabled && validTestEmails.length > 0
                      ? 'bg-amber-500 hover:bg-amber-600'
                      : 'bg-green-500 hover:bg-green-600'
                  }`}
                >
                  {testModeEnabled && validTestEmails.length > 0 ? (
                    <>
                      <FlaskConical className="w-4 h-4" />
                      Send Test
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send {selectedContacts.length} Email{selectedContacts.length !== 1 ? 's' : ''}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {sendProgress.status === 'sending' && (
          <div className="w-full text-center text-xs sm:text-sm text-dark-muted">
            Please wait while emails are being sent...
          </div>
        )}

        {sendProgress.status === 'complete' && (
          <div className="w-full flex justify-center">
            <button
              onClick={onDone}
              className="px-5 sm:px-6 py-2 sm:py-2.5 bg-purple-500 text-white rounded-lg text-sm font-semibold hover:bg-purple-600 transition-all duration-150"
            >
              Done
            </button>
          </div>
        )}

        {sendProgress.status === 'error' && (
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 text-dark-muted hover:text-white transition-all duration-150 text-sm"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
        )}
      </div>
    </div>
  );
};

export default Step4ReviewSend;
